import React, { useState, useEffect } from "react";
import { Info } from "lucide-react";
import { useHistory, useParams } from "react-router-dom";
import Tooltip from "../addentities/Tooltip";
import apiHelper from '../../apiHelper';
import DiscardConfirmationModal from "../../components/DiscardConfirmationModal";

const validateGoogleMapLink = (link) => {
    if (!link) return true; // optional field

    const GOOGLE_LINK_REGEX =
        /^https:\/\/(?:([A-Za-z0-9-]+\.)*google\.[A-Za-z.]+|maps\.app\.goo\.gl)\/.+$/i;

    return GOOGLE_LINK_REGEX.test(link);
};

export default function EditWorkPlaces() {
    const history = useHistory();

    const [formData, setFormData] = useState([]);
    const [disabled, setDisabled] = useState(false);
    const [validationError, setValidationError] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [errors, setFormErrors] = useState({
        workplace_name: '',
        workplace_short_name: '',
        google_map_link: ''
    });
    // Track which “Location Type” is selected:
    const [locationType, setLocationType] = useState("Virtual"); // default to "Virtual" if you like
    const [activeTab, setActiveTab] = useState("Edit Workplace");

    let { id } = useParams();

    useEffect(() => {

        async function fetchWorkplace() {
            const response = await apiHelper(`entities/workplaces-show/${id}/`, 'GET');
            if (response && response.ok) {
                const result = await response.json();
                setLocationType(result.location_type)
                setFormData({
                    workplace_name: result.workplace_name,
                    workplace_short_name: result.workplace_short_name,
                    status: result.status,
                    location_type: result.location_type,
                    virtual_link: result.virtual_link,
                    notes: result.notes,
                    address: {
                        building_name: result.address?.building_name || '',
                        unit_number: result.address?.unit_number || '',
                        street_address: result.address?.street_address || '',
                        street_address_2: result.address?.street_address_2 || '',
                        city: result.address?.city || '',
                        state: result.address?.state || '',
                        postal_code: result.address?.postal_code || '',
                        country: result.address?.country || '',
                        google_map_link: result.address?.google_map_link || '',
                    }
                });


            }

        }
        fetchWorkplace();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name in formData.address) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                address: {
                    ...prevFormData.address,
                    [name]: value
                }
            }));
        } else {
            setFormData((prevFormData) => ({
                ...prevFormData,
                [name]: value
            }));
        }
    };


    const handleSave = async () => {

        setDisabled(true);
        let form_errors = { 
            workplace_name: '', 
            workplace_short_name: '',
            google_map_link: '' 
        };
        let is_error = false;
        if (!formData.workplace_name) {
            form_errors["workplace_name"] = "This field is required!";
            is_error = true;
        }
        if (!formData.workplace_short_name) {
            form_errors["workplace_short_name"] = "This field is required!";
            is_error = true;
        }
        
        // Validate Google Maps link for Onsite locations
        if (locationType === "Onsite" && formData.address?.google_map_link && !validateGoogleMapLink(formData.address.google_map_link)) {
            form_errors["google_map_link"] = "Google link is invalid";
            is_error = true;
        }
        
        setFormErrors(form_errors);
        if (is_error) {
            setDisabled(false);
            return false;
        }

        formData.location_type = locationType;
        const response = await apiHelper(`entities/workplaces-update/${id}/`, 'POST', formData);
        setDisabled(false);

        if (response) {
            if (response.ok) {
                setSuccessMessage('Workplaces update successfully.');
                setValidationError([]);
            }
            else if (response.status == 400) { //for validation error
                const response_errors = await response.json();
                let errors = [];
                for (let i in response_errors) {
                    errors.push(`${i}: ${response_errors[i][0]}`);
                }
                setValidationError(errors);
                setSuccessMessage('');
            }
        }
    };

          const handleDiscard = () => {
        setDiscardModalOpen(true);
    };
    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            history.goBack();
        }
        setDiscardModalOpen(false);
    };
    const handleCancelDiscard = () => {
        setDiscardModalOpen(false);
    };

    const breadcrumbs = [
        "Organisations",
        "Company",
        "Workplaces",
        activeTab
    ];

    return (
        <div className="company-body main-company-body-inner">
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <span
                            key={index}
                            className={`breadcrumb-item ${crumb === "Workplaces" ? "clickable" : ""}`}
                            onClick={() => {
                                if (crumb === "Workplaces") {
                                    history.push("/company", { tab: "workplaces" });
                                }
                            }}
                            style={{ cursor: crumb === "Workplaces" ? "pointer" : "default" }}
                        >
                            {crumb}
                            {!isLast ? "  " : ""}
                        </span>
                    );
                })}
            </div>

            {/* Success Banner */}
{successMessage && (
  <div
    className="d-flex align-items-center justify-content-between"
    role="alert"
    style={{
      boxShadow: "0px 4px 10px 0px #0000001F",
      padding: "15px 10px",
      borderRadius: "12px",
      border: "1px solid #28a745",   // green border
      backgroundColor: "#d4edda",    // light green background
      color: "#155724",              // dark green text
      fontSize: "12px",
      position: "fixed",
      top: "10px",
      width: "calc(100% - 20px)",
      maxWidth: "640px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 2000,
    }}
  >
    <div className="d-flex align-items-center" style={{ gap: "6px" }}>
      <strong>{successMessage}</strong>
    </div>

    <span
      style={{ cursor: "pointer" }}
      onClick={() => setSuccessMessage("")}
    >
      ✕
    </span>
  </div>
)}


            <div className="edit-organisation-info">
                <div className="edit-organisation-left">
                    <h1>Edit Workplace</h1>
                </div>
                <div className="discard-save-organisation-right d-flex align-items-center gap-2">
                    <button
                        className="dark-button"
                        type="button"
                        onClick={handleDiscard}
                    >
                        Discard
                    </button>
                     <DiscardConfirmationModal
                            isOpen={discardModalOpen}
                            onCancel={handleCancelDiscard}
                            onConfirm={handleDiscardConfirm}
                            entityName="Workplace"  // 🔁 Change dynamically (e.g., "Category", "Stock")
                        />
                    <button className="dark-border" type="button" onClick={handleSave} disabled={disabled}>
                        Save
                    </button>
                </div>
            </div>

            <div className="info-section">
                {/* ————— Workplace Info Card ————— */}
                <div className="info-card new-info-overflow-auto">
                    <div className="info-header">
                        <h4 className="info-title">Workplace Info</h4>
                    </div>
                    <div className="info-content d-block">
                        <div className="grid-info-adding">

                            {successMessage && <div style={{ color: 'green' }} className="success-message">{successMessage}</div>}

                            <ul style={{ color: 'red' }}>
                                {validationError && validationError.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>

                            <div className="row gappingcls-added">
                                <div className="col-lg-6 col-md-6 col-12">
                                    <div className="info-inner">
                                        <label htmlFor="workplacename" className="form-label">
                                            Workplace Name <span className="error-color">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control common-input-all"
                                            id="workplacename"
                                            placeholder="Workplace Name"
                                            name="workplace_name"
                                            value={formData.workplace_name}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6 col-12">
                                    <div className="info-inner">
                                        <label htmlFor="status" className="form-label">
                                            Status <span className="error-color">*</span>
                                        </label>
                                        <div className="flexing-type-radio">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="status"
                                                    id="statusActive"
                                                    value="1"
                                                    checked={formData.status == '1'}
                                                    onChange={handleChange}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="statusActive"
                                                >
                                                    Active
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="status"
                                                    id="statusInactive"
                                                    value="0"
                                                    checked={formData.status == '0'}
                                                    onChange={handleChange}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="statusInactive"
                                                >
                                                    Inactive
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row gappingcls-added">
                                <div className="col-lg-6 col-md-6 col-12">
                                    <div className="info-inner">
                                        <label
                                            htmlFor="shortName"
                                            className="form-label d-flex align-items-center gap-2"
                                        >
                                            Short Name
                                            <Tooltip content="A shorter, human-friendly label for your entity">
                                                <Info size={16} className="info-icon" />
                                            </Tooltip>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control common-input-all"
                                            id="shortName"
                                            placeholder="Short Name"
                                            name="workplace_short_name"
                                            value={formData.workplace_short_name}
                                            onChange={handleChange}
                                        />
                                        {errors.workplace_short_name && <p style={{ color: 'red' }}>{errors.workplace_short_name}</p>}
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6 col-12">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label
                                                htmlFor="locationType"
                                                className="form-label d-flex align-items-center gap-1"
                                            >
                                                Location Type <span className="error-color">*</span>
                                            </label>
                                            <div className="flexing-type-radio">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="locationType"
                                                        id="onsite"
                                                        value="Onsite"
                                                        checked={locationType === "Onsite"}
                                                        onChange={() => setLocationType("Onsite")}
                                                    />
                                                    <label className="form-check-label" htmlFor="onsite">
                                                        Onsite
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="locationType"
                                                        id="virtual"
                                                        value="Virtual"
                                                        checked={locationType === "Virtual"}
                                                        onChange={() => setLocationType("Virtual")}
                                                    />
                                                    <label className="form-check-label" htmlFor="virtual">
                                                        Virtual
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* ————— Location Info Card ————— */}
                <div className="info-card new-info-overflow-auto">
                    <div className="info-header">
                        <h4 className="info-title">Location Info</h4>
                    </div>
                    <div className="info-content d-block">
                        <div className="grid-info-adding">
                            {/* Onsite fields (only render when locationType === "Onsite") */}
                            {locationType === "Onsite" && (
                                <>
                                    {/*<div className="row gappingcls-added">
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label htmlFor="building_name" className="form-label">
                                                    Building Name
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    id="building_name"
                                                    placeholder="Building Name"
                                                    name="building_name"
                                                    value={formData.address?.building_name || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label htmlFor="unit_number" className="form-label">
                                                    Unit Number
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    id="unit_number"
                                                    placeholder="Unit Number"
                                                    name="unit_number"
                                                    value={formData.address?.unit_number || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>*/}

                                    {/* Address Line 1 and 2 */}
                                    <div className="row gappingcls-added">
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label
                                                    htmlFor="addressLine1"
                                                    className="form-label d-flex align-items-center gap-2"
                                                >
                                                    Address Line 1
                                                    <Tooltip content="Enter the physical address">
                                                        <Info size={16} className="info-icon" />
                                                    </Tooltip>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    id="addressLine1"
                                                    placeholder="Address Line 1"
                                                    name="street_address"
                                                    value={formData.address?.street_address || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner spacing-top-side-all-head">
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    id="addressLine2"
                                                    placeholder="Address Line 2 (Optional)"
                                                    name="street_address_2"
                                                    value={formData.address?.street_address_2 || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* City and State */}
                                    <div className="row gappingcls-added">
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label htmlFor="city" className="form-label">
                                                    City
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    id="city"
                                                    placeholder="City"
                                                    name="city"
                                                    value={formData.address?.city || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label htmlFor="state" className="form-label">
                                                    State
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    id="state"
                                                    placeholder="State"
                                                    name="state"
                                                    value={formData.address?.state || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Postal Code and Country */}
                                    <div className="row gappingcls-added">
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label htmlFor="postcode" className="form-label">
                                                    Post Code
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    id="postcode"
                                                    placeholder="Post Code"
                                                    name="postal_code"
                                                    value={formData.address?.postal_code || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label htmlFor="country" className="form-label">
                                                    Country
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control common-input-all"
                                                    id="country"
                                                    placeholder="Country"
                                                    name="country"
                                                    value={formData.address?.country || ''}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Google Map Link */}
                                    <div className="row gappingcls-added">
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label
                                                    htmlFor="googlelink"
                                                    className="form-label d-flex align-items-center gap-2"
                                                >
                                                    Google Link
                                                    <Tooltip content="A map link or location URL">
                                                        <Info size={16} className="info-icon" />
                                                    </Tooltip>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control common-input-all ${errors.google_map_link ? 'err-color' : ''}`}
                                                    id="googlelink"
                                                    placeholder="Google Maps URL"
                                                    name="google_map_link"
                                                    value={formData.address?.google_map_link || ''}
                                                    onChange={handleChange}
                                                />
                                                {errors.google_map_link && <p style={{ color: 'red' }}>{errors.google_map_link}</p>}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}



                            {/* Virtual fields (only render when locationType === "Virtual") */}
                            {locationType === "Virtual" && (
                                <div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label
                                                htmlFor="virtualLink"
                                                className="form-label d-flex align-items-center gap-2"
                                            >
                                                Link <span className="error-color">*</span>
                                                <Tooltip content="Enter the meeting or virtual link">
                                                    <Info size={16} className="info-icon" />
                                                </Tooltip>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control	common-input-all"
                                                id="virtualLink"
                                                placeholder="https://..."
                                                name="virtual_link"
                                                value={formData.virtual_link}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ————— Additional Info (Note) ————— */}
                <div className="info-card">
                    <div className="info-header">
                        <h4 className="info-title">Additional Info</h4>
                    </div>
                    <div className="info-content">
                        <div className="info-inner common-textarea">
                            <label
                                htmlFor="exampleFormControlTextarea1"
                                className="form-label"
                            >
                                Note
                            </label>
                            <textarea
                                className="form-control	w-100 common-textarea-all"
                                id="exampleFormControlTextarea1"
                                rows="3"
                                placeholder="Any additional notes..."
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}